// Mock data service for the fraud detection dashboard

export interface Lead {
  id: string
  timestamp: Date
  name: string
  email: string
  phone: string
  zip: string
  ip: string
  riskScore: number
  status: 'approved' | 'rejected' | 'pending'
  flags: string[]
}

export interface PatternAlert {
  id: string
  type: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  count: number
  status: 'increasing' | 'decreasing' | 'stable'
  description: string
}

export interface TodayStats {
  totalLeads: number
  fraudBlocked: number
  moneySaved: number
  detectionRate: number
  falsePositives: number
  avgDetectionTime: number
}

export interface DashboardData {
  todayStats: TodayStats
  recentLeads: Lead[]
  activePatterns: PatternAlert[]
}

// Fraud patterns based on real data analysis
const FRAUD_PATTERNS = {
  SEQUENTIAL_PHONE: ['sequential_phone'],
  DATACENTER_IP: ['datacenter_ip'],
  ZIP_CONCENTRATION: ['zip_concentration'],
  VELOCITY_ATTACK: ['velocity_attack'],
  SUSPICIOUS_EMAIL: ['suspicious_email']
}

const SUSPICIOUS_DOMAINS = ['armyspy.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com']
const DATACENTER_IPS = ['169.197.85.', '52.144.', '104.248.', '167.99.']
const HIGH_RISK_ZIPS = ['80112', '10001', '90210', '33101']

// Generate realistic names and emails
const FIRST_NAMES = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Lisa', 'Robert', 'Jennifer', 'William', 'Amanda']
const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez']

export const generateMockLead = (): Lead => {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)]
  const name = `${firstName} ${lastName}`
  
  // Generate email with potential fraud patterns
  const isSuspiciousDomain = Math.random() < 0.15
  const domain = isSuspiciousDomain 
    ? SUSPICIOUS_DOMAINS[Math.floor(Math.random() * SUSPICIOUS_DOMAINS.length)]
    : `${Math.random() < 0.5 ? 'gmail' : Math.random() < 0.5 ? 'yahoo' : 'hotmail'}.com`
  
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}${Math.floor(Math.random() * 999)}@${domain}`
  
  // Generate phone with potential sequential pattern
  const isSequential = Math.random() < 0.08
  const basePhone = isSequential ? '2262433' : `${Math.floor(Math.random() * 800) + 200}${Math.floor(Math.random() * 900) + 100}`
  const phone = isSequential 
    ? `${basePhone}${Math.floor(Math.random() * 10) + 170}` // Sequential range 170-179
    : `${basePhone}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
  
  // Generate IP with potential datacenter pattern
  const isDatacenterIP = Math.random() < 0.12
  const ip = isDatacenterIP
    ? `${DATACENTER_IPS[Math.floor(Math.random() * DATACENTER_IPS.length)]}${Math.floor(Math.random() * 255)}`
    : `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`
  
  // Generate zip with potential concentration
  const isHighRiskZip = Math.random() < 0.25
  const zip = isHighRiskZip
    ? HIGH_RISK_ZIPS[Math.floor(Math.random() * HIGH_RISK_ZIPS.length)]
    : Math.floor(Math.random() * 99999).toString().padStart(5, '0')
  
  // Determine flags based on patterns
  const flags: string[] = []
  if (isSequential) flags.push('sequential_phone')
  if (isDatacenterIP) flags.push('datacenter_ip')
  if (isSuspiciousDomain) flags.push('suspicious_email')
  if (isHighRiskZip && zip === '80112') flags.push('zip_concentration')
  if (Math.random() < 0.05) flags.push('velocity_attack')
  
  // Calculate risk score based on flags
  let riskScore = Math.floor(Math.random() * 30) + 10 // Base score 10-40
  if (flags.includes('sequential_phone')) riskScore += 35
  if (flags.includes('datacenter_ip')) riskScore += 25
  if (flags.includes('suspicious_email')) riskScore += 20
  if (flags.includes('zip_concentration')) riskScore += 30
  if (flags.includes('velocity_attack')) riskScore += 40
  
  riskScore = Math.min(riskScore, 99) // Cap at 99
  
  // Determine status based on risk score
  let status: 'approved' | 'rejected' | 'pending' = 'pending'
  if (riskScore >= 80) status = 'rejected'
  else if (riskScore <= 20) status = 'approved'
  
  return {
    id: `lead_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    name,
    email,
    phone,
    zip,
    ip,
    riskScore,
    status,
    flags
  }
}

export const generateMockDashboardData = async (): Promise<DashboardData> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500))
  
  const todayStats: TodayStats = {
    totalLeads: 247,
    fraudBlocked: 79,
    moneySaved: 2370, // 79 * $30
    detectionRate: 95.3,
    falsePositives: 1.8,
    avgDetectionTime: 47
  }
  
  const recentLeads: Lead[] = Array.from({ length: 25 }, () => generateMockLead())
  
  const activePatterns: PatternAlert[] = [
    {
      id: '1',
      type: 'Sequential Phones',
      severity: 'critical',
      count: 12,
      status: 'increasing',
      description: 'Phone sequences detected in range 2262433171-3177'
    },
    {
      id: '2',
      type: 'Data Center IPs',
      severity: 'high', 
      count: 34,
      status: 'stable',
      description: 'Traffic from hosting providers (169.197.85.x range)'
    },
    {
      id: '3',
      type: 'Zip Concentration',
      severity: 'critical',
      count: 43,
      status: 'increasing', 
      description: 'High concentration in 80112 zip code'
    },
    {
      id: '4',
      type: 'Velocity Attacks',
      severity: 'medium',
      count: 7,
      status: 'decreasing',
      description: '3+ submissions within 60 seconds'
    }
  ]
  
  return {
    todayStats,
    recentLeads,
    activePatterns
  }
}